import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Drawer,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
  message as antMessage,
} from 'antd';
import {
  AppstoreOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { buildAppDelete, buildAppGet, buildAppList, buildAppRename } from './buildAppApi';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

const PLAN_TYPE_COLORS = {
  workflow: 'purple',
  eform: 'blue',
  metaflow: 'violet',
  'workflow+eform': 'purple',
  'workflow+metaflow': 'purple',
};

const PLAN_TYPE_LABELS = {
  workflow: '工作流程',
  eform: '電子表單',
  metaflow: 'Meta Flow',
  'workflow+eform': '流程+表單',
  'workflow+metaflow': '流程+MetaFlow',
};

const STATUS_COLORS = { Draft: 'orange', Complete: 'green' };
const STATUS_LABELS = { Draft: '草稿', Complete: '已完成' };

/**
 * Apps 歷史面板 — Drawer 顯示已保存的 BuildApp sessions。
 * Props:
 *   open          {boolean}
 *   onClose       {()=>void}
 *   onLoadApp     {(appData, chatHistory)=>void} — 通知父元件恢復 session
 */
const BuildAppHistoryPanel = ({ open, onClose, onLoadApp }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await buildAppList(1, 50);
      setItems(data.data || []);
      setTotal(data.total || 0);
    } catch {
      antMessage.error('載入 Apps 歷史失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const handleLoad = useCallback(async (item) => {
    setLoadingId(item.id);
    try {
      const result = await buildAppGet(item.id);
      onLoadApp?.(result.app, result.chatHistory || []);
      onClose?.();
    } catch {
      antMessage.error('載入應用失敗');
    } finally {
      setLoadingId(null);
    }
  }, [onLoadApp, onClose]);

  const handleDelete = useCallback(async (id) => {
    try {
      await buildAppDelete(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      antMessage.success('已刪除');
    } catch {
      antMessage.error('刪除失敗');
    }
  }, []);

  const startRename = useCallback((item) => {
    setRenamingId(item.id);
    setRenameValue(item.name || '');
  }, []);

  const confirmRename = useCallback(async (id) => {
    const name = renameValue.trim();
    if (!name) { setRenamingId(null); return; }
    try {
      await buildAppRename(id, name);
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, name } : i));
      antMessage.success('已重命名');
    } catch {
      antMessage.error('重命名失敗');
    } finally {
      setRenamingId(null);
    }
  }, [renameValue]);

  const renderResourceTags = (item) => {
    const tags = [];
    if (item.workflowId) tags.push({ label: '流程', color: 'purple' });
    if (item.eformId) tags.push({ label: '表單', color: 'blue' });
    if (item.metaflowId) tags.push({ label: 'MetaFlow', color: 'geekblue' });
    if (item.templateId) tags.push({ label: '模板', color: 'cyan' });
    if (item.datasetId) tags.push({ label: 'Dataset', color: 'green' });
    if (item.aiStaffId) tags.push({ label: 'AI 員工', color: 'gold' });
    if (item.kbDocumentIds && item.kbDocumentIds !== '[]') tags.push({ label: 'KB', color: 'lime' });
    return tags.map((t, i) => <Tag key={i} color={t.color} style={{ fontSize: 11, padding: '0 5px', marginBottom: 2 }}>{t.label}</Tag>);
  };

  return (
    <Drawer
      title={
        <Space>
          <HistoryOutlined />
          <span>Apps 歷史</span>
          <Badge count={total} overflowCount={99} style={{ backgroundColor: '#7234CF' }} />
        </Space>
      }
      open={open}
      onClose={onClose}
      width={440}
      extra={
        <Button icon={<ReloadOutlined />} size="small" onClick={fetchList} loading={loading}>
          重新整理
        </Button>
      }
    >
      {loading && items.length === 0 ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : items.length === 0 ? (
        <Empty
          image={<AppstoreOutlined style={{ fontSize: 48, color: '#d8b4fe' }} />}
          imageStyle={{ height: 56 }}
          description={
            <Text type="secondary">尚未有保存的 Apps<br />開始建立後會自動保存</Text>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((item) => (
            <div key={item.id} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renamingId === item.id ? (
                    <Input
                      autoFocus
                      size="small"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => confirmRename(item.id)}
                      onPressEnter={() => confirmRename(item.id)}
                      style={{ width: '100%', marginBottom: 4 }}
                    />
                  ) : (
                    <div style={styles.nameRow}>
                      <Text
                        strong
                        ellipsis={{ tooltip: item.name }}
                        style={{ fontSize: 14, flex: 1, minWidth: 0 }}
                      >
                        {item.name || '未命名'}
                      </Text>
                      <Tooltip title="重命名">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => startRename(item)}
                          style={{ flexShrink: 0 }}
                        />
                      </Tooltip>
                    </div>
                  )}
                  <Space size={4} wrap style={{ marginTop: 2 }}>
                    {item.planType && (
                      <Tag color={PLAN_TYPE_COLORS[item.planType] || 'default'} style={{ fontSize: 11 }}>
                        {PLAN_TYPE_LABELS[item.planType] || item.planType}
                      </Tag>
                    )}
                    <Tag color={STATUS_COLORS[item.status] || 'default'} style={{ fontSize: 11 }}>
                      {STATUS_LABELS[item.status] || item.status}
                    </Tag>
                  </Space>
                </div>
              </div>

              {item.summary && (
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  {item.summary.length > 80 ? item.summary.slice(0, 80) + '…' : item.summary}
                </Text>
              )}

              <div style={{ marginTop: 6 }}>
                {renderResourceTags(item)}
              </div>

              <div style={styles.cardBottom}>
                <Space size={4}>
                  <ClockCircleOutlined style={{ color: '#aaa', fontSize: 11 }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {dayjs(item.updatedAt).fromNow()}
                  </Text>
                </Space>
                <Space size={4}>
                  <Popconfirm
                    title="確定刪除此應用？"
                    description="刪除後不可恢復，但已建立的流程/表單等資源不受影響。"
                    okText="刪除"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(item.id)}
                  >
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    loading={loadingId === item.id}
                    onClick={() => handleLoad(item)}
                    style={{ background: '#7234CF', borderColor: '#7234CF' }}
                  >
                    載入
                  </Button>
                </Space>
              </div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #f0e6ff',
    borderRadius: 10,
    padding: '12px 14px',
    boxShadow: '0 1px 4px rgba(114,52,207,0.06)',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  cardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid #f9f0ff',
  },
};

export default BuildAppHistoryPanel;
